import React, { useContext, useEffect, useState } from "react";
import Button from "../ui/button/Button";
import Select from "../ui/select/Select";
import InputText from "../ui/inputText/InputText";
import { useTranslation } from "react-i18next";
import { ISettingUser } from "../../types/settings";
import { RolesContext } from "../../context/rolesContext";
import { IRolesContext } from "../../types/roles";
import { ModifyUserErrorObj, validateModifyUserData } from "../../utils/helpers/validations/validateModifyUserData";
import { canPass } from "../../utils/helpers/validations/canPass";
import { settingsService } from "../../api/settingsService/settingsService";
import { toast } from "react-toastify";

type Props = {
    loggedUserIsAccountAdmin: boolean;
    user: ISettingUser | null;
    isOpen: boolean;
    callbackCloseSidebar: () => any;
    callbackUpdateContacts: (contacts: ISettingUser[]) => any;
}

const SidebarModifyUser: React.FC<Props> = ({
    loggedUserIsAccountAdmin,
    isOpen,
    user,
    callbackCloseSidebar,
    callbackUpdateContacts
}) => {
    const { t } = useTranslation();

    const { roles } = useContext(RolesContext) as IRolesContext;

    const [loading, setLoading] = useState(false);
    const [rolesOptions, setRolesOptions] = useState<{ value: number, label: string }[]>([]);
    const [userData, setUserData] = useState<null | ISettingUser>(null);
    const [errorState, setErrorState] = useState<ModifyUserErrorObj>({
        name: {
            error: false,
            message: ''
        },
        surname: {
            error: false,
            message: ''
        },
        phone: {
            error: false,
            message: ''
        },
        role: {
            error: false,
            message: ''
        }
    })

    useEffect(() => {
        if (!!roles) {
            setRolesOptions([...roles].map((role) => {
                return {
                    label: role.value,
                    value: role.id
                }
            }));
        }
    }, [roles]);

    useEffect(() => {
        if (!!user && isOpen) {
            setUserData(user);
        }
    }, [isOpen]);

    const onChangeText = (e: any, stateName: "name" | "surname" | "phone" | "role") => {
        if (stateName !== "role") {
            if (e !== undefined && !!userData) {
                setUserData({
                    ...userData,
                    [stateName]: e
                });
            }
        } else {
            if (e !== undefined && !!userData) {
                const role = rolesOptions.find(o => o.value == e.target.value);

                if (!!role) {
                    setUserData({
                        ...userData,
                        "role": {
                            id: role.value,
                            value: role.label
                        }
                    });
                }
            }
        }
    }

    const handleModifyUserRequest = async () => {
        if (!!userData && loggedUserIsAccountAdmin === true) {
            setLoading(true);

            const errorObj = validateModifyUserData(userData);

            const canGo = canPass(errorObj);
            if (canGo === true) {
                const modifyUserResponse = await settingsService.modifyContact({
                    id: userData?.id,
                    name: userData.name,
                    phone: parseInt(userData.phone).toString(),
                    role: userData.role.id,
                    surname: userData.surname
                });

                if (modifyUserResponse.success) {
                    const getUsersResponse = await settingsService.getContacts();

                    if (getUsersResponse.success) {
                        callbackUpdateContacts(getUsersResponse.responseBody.contacts);
                        callbackCloseSidebar();
                    } else {
                        window.location.reload();
                    }
                } else {
                    toast.error(t('errors.errorModifyingContact'))
                }
            } else {
                setErrorState(errorObj);
            }

            setLoading(false);
        }
    }

    const resetError = (errorName: "phone" | "role" | "name" | "surname") => () => {
        setErrorState({
            ...errorState,
            [errorName]: {
                error: false,
                message: ''
            }
        })
    }

    return (
        <aside
            className={"flex flex-col transition z-10 fixed right-0 top-0 h-[100dvh] w-screen md:w-[520px] bg-white overflow-auto hide-scrollbar" + (isOpen === true ? " translate-x-0 sidebar-shadow" : " translate-x-[100%]")}
        >
            <div className="shrink-0 h-[88px] px-[30px] pt-[32px] pb-[24px] border-b flex justify-between items-center">
                <div className="text-lg font-bold leading-[21.6px]">
                    {t('common.sidebars.modifyUser.heading')}
                </div>
                <div onClick={callbackCloseSidebar} className="cursor-pointer">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
            </div>

            {/* MAIN CONTENT OF SIDEBAR */}
            <div className="grow overflow-auto px-[30px] py-8">
                {
                    !!userData &&
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 lg:col-span-6">
                            <InputText
                                name="name"
                                label={t('common.sidebars.modifyUser.name')}
                                placeholder={t('common.sidebars.modifyUser.name') as string}
                                value={userData.name}
                                callbackOnChange={(e) => onChangeText(e, "name")}
                                isRequired={true}
                                hasError={errorState.name.error}
                                errorMessage={errorState.name.message}
                                callbackOnFocus={resetError('name')}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <InputText
                                name="surname"
                                label={t('common.sidebars.modifyUser.surname')}
                                placeholder={t('common.sidebars.modifyUser.surname') as string}
                                value={userData.surname}
                                callbackOnChange={(e) => onChangeText(e, "surname")}
                                isRequired={true}
                                hasError={errorState.surname.error}
                                errorMessage={errorState.surname.message}
                                callbackOnFocus={resetError('surname')}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <InputText
                                type="tel"
                                name="phone"
                                label={t('common.sidebars.modifyUser.phone')}
                                placeholder={t('common.sidebars.modifyUser.phone') as string}
                                value={userData.phone}
                                callbackOnChange={(e) => onChangeText(e, "phone")}
                                isRequired={true}
                                hasError={errorState.phone.error}
                                errorMessage={errorState.phone.message}
                                callbackOnFocus={resetError('phone')}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <Select
                                callbackOnChange={(e) => onChangeText(e, "role")}
                                label={t('common.sidebars.modifyUser.role')}
                                name="role"
                                value={rolesOptions.find(o => o.value === userData.role.id)?.value}
                                placeholder={t('common.sidebars.modifyUser.role')}
                                options={rolesOptions}
                                isRequired={true}
                                hasError={errorState.role.error}
                                errorMessage={errorState.role.message}
                                callbackOnFocus={resetError('role')}
                            />
                        </div>
                    </div>
                }
            </div>

            <div className="shrink-0 py-6 px-[30px] text-right border-t">
                <Button
                    callback={handleModifyUserRequest}
                    loading={loading}
                >
                    {t('common.sidebars.saveButtonText')}
                </Button>
            </div>
        </aside>
    );
}

export default SidebarModifyUser;